import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/layout/PageHeader';
import { ScrollTable } from '@/components/layout/ScrollTable';
import { apiFetch, ApiError } from '@/lib/api';
import { useT } from '@/lib/i18n';

interface TenantRow {
  id: string;
  name: string;
  status: string;
  created_at: string;
  contact_email?: string;
}

interface ListResp {
  tenants: TenantRow[];
}

export function Tenants() {
  const t = useT();
  const nav = useNavigate();
  const [rows, setRows] = useState<TenantRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch<ListResp>('/tenants');
        setRows(resp.tenants ?? []);
      } catch (e) {
        const msg = e instanceof ApiError ? e.message : 'failed';
        setErr(msg);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('superadmin.tenants.title')}
        subtitle={t('superadmin.tenants.subtitle')}
        actions={
          <Button onClick={() => nav('/superadmin/tenants/new')}>
            <Plus className="mr-1 h-4 w-4" />
            {t('superadmin.tenants.new')}
          </Button>
        }
      />
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-muted-foreground">
              {t('auth.loading')}
            </div>
          ) : err ? (
            <div className="p-6 text-sm text-destructive">{err}</div>
          ) : rows.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">
              {t('superadmin.tenants.empty')}
            </div>
          ) : (
            <ScrollTable>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('superadmin.tenants.colId')}</TableHead>
                    <TableHead>{t('superadmin.tenants.colName')}</TableHead>
                    <TableHead>{t('superadmin.tenants.colStatus')}</TableHead>
                    <TableHead className="hidden md:table-cell">
                      {t('superadmin.tenants.colCreated')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('superadmin.tenants.colActions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(row => (
                    <TableRow key={row.id}>
                      <TableCell className="font-mono text-xs">
                        {row.id}
                      </TableCell>
                      <TableCell className="font-medium">{row.name}</TableCell>
                      <TableCell>
                        <Badge
                          variant={
                            row.status === 'active' ? 'default' : 'secondary'
                          }
                        >
                          {row.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell text-muted-foreground">
                        {row.created_at?.slice(0, 10)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button asChild size="sm" variant="ghost">
                          <Link to={`/superadmin/tenants/${row.id}`}>
                            {t('superadmin.tenants.view')}
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ScrollTable>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
